/**
 * Every state this photo has been in, and one click back to any of them.
 *
 * Captions are computed by comparing each recipe with the one before it, so a
 * step is described by what actually changed rather than by a label written at
 * the call site. Steps after the current one are kept visible instead of being
 * thrown away, because a redo you can see is worth more than one you have to
 * remember.
 */

import { useMemo, type CSSProperties } from 'react';
import { describeStep } from '@lumos/core';
import { useSelectedPhoto, useStore } from '../../state/store.ts';
import { Panel } from '../controls/Panel.tsx';

const LIST: CSSProperties = { listStyle: 'none', margin: 0, padding: 0 };

/** Rows past this point are older than anyone scrolls for. */
const VISIBLE = 40;

export function HistoryPanel() {
  const photo = useSelectedPhoto();
  const jumpTo = useStore((state) => state.jumpTo);

  const history = photo?.history;
  const captions = useMemo(
    () =>
      history
        ? history.map((recipe, index) =>
            index === 0 ? 'Opened' : describeStep(history[index - 1], recipe),
          )
        : [],
    [history],
  );

  if (!photo || captions.length === 0) return null;

  const current = photo.historyIndex;
  const first = Math.max(0, captions.length - VISIBLE);
  const shown: number[] = [];
  for (let index = captions.length - 1; index >= first; index -= 1) shown.push(index);
  const older = first;

  return (
    <Panel id="history" title="History">
      <ul className="history" style={LIST}>
        {shown.map((index) => {
          const state = index === current ? 'current' : index > current ? 'future' : '';
          return (
            <li key={index}>
              <button
                type="button"
                className={`history-step${state ? ` ${state}` : ''}`}
                onClick={() => jumpTo(index)}
                aria-current={index === current ? 'step' : undefined}
                aria-label={index > current ? `${captions[index]}, undone` : captions[index]}
              >
                <span className="history-dot" aria-hidden="true" />
                <span className="history-label">{captions[index]}</span>
              </button>
            </li>
          );
        })}
      </ul>
      {older > 0 ? (
        <p className="edits-empty">
          {older} older step{older === 1 ? '' : 's'} before this.
        </p>
      ) : null}
    </Panel>
  );
}
